import React from 'react';

import clsx from 'clsx';
import { Box, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';

export type ChatCardTone = 'brand' | 'success' | 'warning' | 'error' | 'neutral';

export interface ChatCardProps {
  // The card's name for screen readers, e.g. "Invite draft".
  label: string;
  icon?: React.ReactNode;
  iconTone?: ChatCardTone;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  // Where the card is at, beside the title: "Draft", "Sending".
  badge?: React.ReactNode;
  badgeTone?: ChatCardTone;
  // The card's sections (ChatCardSection), between the header and the footer.
  children?: React.ReactNode;
  // Its buttons, and any note under them.
  footer?: React.ReactNode;
  className?: string;
}

// A card under a message: something to check, pick from or act on rather
// than read, such as an invite Laila drafted. One card stands for one thing
// and changes in place as that thing does.
export const ChatCard: React.FC<ChatCardProps> = ({
  label,
  icon,
  iconTone = 'brand',
  title,
  subtitle,
  badge,
  badgeTone = 'brand',
  children,
  footer,
  className,
}) => {
  const classes = useStyles();
  const tones = useToneStyles();

  return (
    <Box component="section" aria-label={label} className={clsx(classes.card, className)}>
      <Box className={clsx(classes.header, { [classes.headerAlone]: !children && !footer })}>
        {icon && <Box className={clsx(classes.icon, tones[iconTone])}>{icon}</Box>}
        <Box className={classes.heading}>
          <Typography component="h3" className={classes.title}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" component="p" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {badge && <ChatCardBadge tone={badgeTone}>{badge}</ChatCardBadge>}
      </Box>
      {children}
      {footer && <Box className={classes.footer}>{footer}</Box>}
    </Box>
  );
};

// A short state in a tinted pill: "Draft", "Expired". Also for rows inside a card.
export const ChatCardBadge: React.FC<{ tone?: ChatCardTone; children: React.ReactNode }> = ({
  tone = 'brand',
  children,
}) => {
  const classes = useStyles();
  const tones = useToneStyles();
  return <span className={clsx(classes.badge, tones[tone])}>{children}</span>;
};

interface SectionProps {
  // A small heading: "Who · 3", "Where".
  title?: React.ReactNode;
  children?: React.ReactNode;
}

export const ChatCardSection: React.FC<SectionProps> = ({ title, children }) => {
  const classes = useStyles();

  return (
    <Box className={classes.section}>
      {title && (
        <Typography variant="caption" color="textSecondary" component="h4" className={classes.sectionTitle}>
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
};

const useToneStyles = makeStyles((theme: Theme) =>
  createStyles({
    brand: { color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.12) },
    success: { color: theme.palette.success.main, backgroundColor: alpha(theme.palette.success.main, 0.14) },
    // Warning yellow is too light for text on either theme: it tints, the text stays readable.
    warning: { color: theme.palette.text.primary, backgroundColor: alpha(theme.palette.warning.main, 0.24) },
    error: { color: theme.palette.error.main, backgroundColor: alpha(theme.palette.error.main, 0.12) },
    neutral: {
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      width: 540,
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      borderRadius: 18,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 1px 1px ${alpha(theme.palette.text.primary, 0.08)}`,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '14px 16px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerAlone: { borderBottom: 0 },
    icon: {
      flex: 'none',
      width: 34,
      height: 34,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 18,
    },
    heading: { flex: 1, minWidth: 0 },
    title: { fontSize: 15, fontWeight: 600, lineHeight: 1.35 },
    subtitle: { display: 'block', marginTop: 2 },
    badge: {
      flex: 'none',
      fontSize: 12,
      fontWeight: 600,
      padding: '3px 9px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      padding: '12px 16px',
      fontSize: 14,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': { borderBottom: 0 },
    },
    sectionTitle: { fontWeight: 600 },
    footer: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '12px 16px' },
  })
);
